import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxOptional } from '@onyx/angular';
import {
  OnyxDataWidgetComponent,
  OnyxDatePipe,
  OnyxInformationListComponent,
  OnyxInformationRowComponent,
  OnyxLinkComponent,
  OnyxModalComponent,
  OnyxNamePipe,
  OnyxPhonePipe,
  OnyxTimePipe,
} from '@onyx/angular';
import { Subject } from 'rxjs';
import { DictionaryCode } from '../../../../../common/enums/dictionary-code';
import { CommonHelper } from '../../../../../common/helpers/common.helper';
import type { Point } from '../../../../../common/interfaces/common/point';
import { DriverHelper } from '../../../../drivers/common/helpers/driver.helper';
import { TechnicalPointCategory } from '../../../../fleet/common/enums/technical-point-category';
import { OrderPointCategory } from '../../enums/order-point-category';
import { OrderHelper } from '../../helpers/order.helper';
import type {
  CompletedReport,
  InProgressReport,
} from '../../interfaces/order-point-driver-reports';
import { RouteHelper } from './../../../../../common/helpers/route.helper';
import { DriverReportModalImageComponent } from './driver-report-modal-image/driver-report-modal-image.component';

export interface DriverReportModalData {
  point: Point;
  typeIndex: number | null;
  orderUuid: string | null;
  orderIdentifier: string | null;
}

export interface DriverReportModalFileData {
  files: string[];
  images: File[];
  title: string;
  note?: string;
}

@Component({
  selector: 'app-driver-report-modal',
  imports: [
    OnyxModalComponent,
    OnyxInformationListComponent,
    OnyxInformationRowComponent,
    TranslatePipe,
    OnyxNamePipe,
    OnyxPhonePipe,
    OnyxDataWidgetComponent,
    OnyxTimePipe,
    OnyxLinkComponent,
    OnyxDatePipe,
    NgTemplateOutlet,
    DriverReportModalImageComponent,
  ],
  templateUrl: './driver-report-modal.component.html',
  styleUrl: './driver-report-modal.component.scss',
})
export class DriverReportModalComponent {
  protected readonly data = inject<DriverReportModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef<void>);
  protected readonly driverHelper = inject(DriverHelper);
  private readonly orderHelper = inject(OrderHelper);

  protected readonly I18N = 'orders.driverReportModal';

  protected readonly DictionaryCode = DictionaryCode;
  protected readonly CommonHelper = CommonHelper;
  protected readonly TechnicalPointCategory = TechnicalPointCategory;
  protected readonly RouteHelper = RouteHelper;

  protected inProgressFiles = computed(() =>
    this.getFilteredFiles(this.data.point.driverReports?.['in-progress']),
  );
  protected completedFiles = computed(() =>
    this.getFilteredFiles(this.data.point.driverReports?.completed),
  );

  protected loading = signal(false);

  protected close$ = new Subject<void>();

  protected isLoadingPoint = computed(
    () => this.data.point.category === OrderPointCategory.LOADING,
  );

  protected openOrderModal(): void {
    const uuid = this.data.orderUuid;
    if (uuid) this.orderHelper.openModal(uuid);
  }

  protected openDriverModal(): void {
    const author = this.data.point.driverReports?.completed?.author.uuid;
    if (author) this.driverHelper.openModal(author);
  }

  private getFilteredFiles(
    report: OnyxOptional<InProgressReport | CompletedReport>,
  ) {
    return OrderHelper.getDriverReportRawFilesData(report ?? null).filter(
      (item) => !!item.files.length,
    );
  }
}
